import { useMemo, useState } from "react";
import type {
  Coverage,
  LeaveBalance,
  RaiseTimeOff,
  StaffMember,
  TimeOffRequest,
} from "@dentacore/shared-types";
import { TIME_OFF_TYPES } from "@dentacore/shared-types";

import { Modal } from "../ui/modal";
import { Pager, useFitPages, useHeight } from "../ui/paging";
import { HelpTip } from "../ui/popovers";
import { dayAndMonth, daysText } from "./roster";
import styles from "./staff.module.css";
import { RequiredMark } from "../ui/required-mark";

/** A request row with nothing extra on it; measured rows replace this as they draw. */
const ROW_ESTIMATE = 66;
/** A card's heading plus its two borders. */
const CARD_HEAD = 54;
/** Room kept for a pager under a list that needs one. */
const PAGER = 44;
/** Space between the two cards. */
const GAP = 14;

/**
 * Time off — STORY-096, laid out as ST1 and kept that way (ST14–ST16).
 *
 * **What waits on a decision comes first**, newest asked at the top, one
 * request a line with the decision on its right — and, since ST14, how much annual leave that person
 * has left beside it, so the answer is made knowing it. Decided requests fold
 * into one line with a count and open under the queue, newest decision first,
 * each keeping *Reopen*.
 *
 * **One queue for the clinic, not one per location**: `time_off_requests`
 * carries no branch, and somebody asking for Thursday off is asking for all of
 * Thursday, everywhere they work.
 *
 * **The row says whether *you* may decide it** (S8), rather than the screen
 * guessing, so the button and the endpoint never disagree.
 *
 * **Both lists page to fit** (ST16): the waiting queue grows with its requests
 * up to the room it has — all of it while Decided is folded, half once it is
 * open — and Decided takes the rest.
 */
export function TimeOffView({
  requests,
  balances,
  busy,
  onDecide,
  onReopen,
  coverage,
}: {
  requests: readonly TimeOffRequest[] | null;
  balances: readonly LeaveBalance[] | null;
  busy: boolean;
  onDecide: (id: string, what: "approve" | "decline", acknowledge?: boolean) => void;
  onReopen: (id: string) => void;
  /** The warning held back from an approval, keyed by request. */
  coverage: Record<string, Coverage>;
}): React.JSX.Element {
  const [showDecided, setShowDecided] = useState(false);
  const [columnRef, columnHeight] = useHeight<HTMLDivElement>();

  // Newest asked first, so a request just made is at the top of the queue.
  const pending = [...(requests?.filter((row) => row.status === "PENDING") ?? [])].sort((a, b) =>
    b.filedAt.localeCompare(a.filedAt),
  );
  const decided = [...(requests?.filter((row) => row.status !== "PENDING") ?? [])].sort((a, b) =>
    (b.decidedAt ?? "").localeCompare(a.decidedAt ?? ""),
  );
  const approved = decided.filter((row) => row.status === "APPROVED").length;
  const balanceOf = new Map((balances ?? []).map((row) => [row.userId, row]));

  // Before the column has a height, everything is one page.
  const waitingBudget =
    columnHeight <= 0
      ? undefined
      : Math.max(
          ROW_ESTIMATE,
          showDecided
            ? Math.floor((columnHeight - GAP) / 2) - CARD_HEAD - PAGER
            : columnHeight - CARD_HEAD - GAP - CARD_HEAD - PAGER,
        );

  // A coverage warning makes its row taller; the pager follows the row onto
  // whichever page now holds it rather than let it slip off this one.
  const warned = Object.keys(coverage).join(",");
  const follow = useMemo(() => {
    const last = warned
      .split(",")
      .filter((id) => id !== "")
      .pop();
    return last === undefined ? null : { key: last };
  }, [warned]);

  const waiting = useFitPages({
    follow,
    keys: pending.map((row) => row.id),
    estimate: () => ROW_ESTIMATE,
    gap: 0,
    budget: waitingBudget ?? Number.MAX_SAFE_INTEGER,
    resetOn: showDecided,
  });
  const done = useFitPages({
    keys: decided.map((row) => row.id),
    estimate: () => ROW_ESTIMATE,
    gap: 0,
    resetOn: showDecided,
  });

  const row = (
    request: TimeOffRequest,
    measure: (element: HTMLElement | null) => (() => void) | undefined,
  ): React.JSX.Element => (
    <RequestRow
      key={request.id}
      row={request}
      measure={measure}
      balance={request.status === "PENDING" ? (balanceOf.get(request.userId) ?? null) : null}
      busy={busy}
      onDecide={onDecide}
      onReopen={onReopen}
      coverage={coverage[request.id] ?? null}
    />
  );

  return (
    <div className={styles.toColumn} ref={columnRef} data-testid="time-off">
      <section className={styles.toCard}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>Waiting on a decision</h3>
          {pending.length > 0 && <span className={styles.cardCount}>{pending.length}</span>}
        </div>
        {requests === null ? (
          <p className={styles.blank}>Reading&hellip;</p>
        ) : pending.length === 0 ? (
          <p className={styles.blank} data-testid="no-pending">
            Nothing is waiting on a decision.
          </p>
        ) : (
          <div className={styles.toList}>
            {pending
              .slice(...waiting.range)
              .map((request) => row(request, waiting.measure(request.id)))}
          </div>
        )}
        {waiting.pages.length > 1 && (
          <div className={styles.cardFoot}>
            <Pager
              page={waiting.page}
              count={waiting.pages.length}
              onPage={waiting.setPage}
              from={waiting.range[0] + 1}
              to={waiting.range[1]}
              total={pending.length}
              noun="requests"
              testId="waiting-pager"
            />
          </div>
        )}
      </section>

      {decided.length > 0 && (
        <section
          className={showDecided ? `${styles.toCard} ${styles.toCardGrow}` : styles.toCard}
          data-testid="decided"
        >
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Decided</h3>
            <span className={styles.cardNote}>
              {decided.length} · {approved} approved, {decided.length - approved} declined
            </span>
            <button
              type="button"
              className={`${styles.ghost} ${styles.small} ${styles.headEnd}`}
              aria-expanded={showDecided}
              onClick={() => setShowDecided((was) => !was)}
              data-testid="decided-toggle"
            >
              {showDecided ? "Hide" : "Show"}
            </button>
          </div>
          {showDecided && (
            <>
              <div className={styles.toArea} ref={done.areaRef}>
                {decided
                  .slice(...done.range)
                  .map((request) => row(request, done.measure(request.id)))}
              </div>
              <div className={styles.cardFoot}>
                <Pager
                  page={done.page}
                  count={done.pages.length}
                  onPage={done.setPage}
                  from={done.range[0] + 1}
                  to={done.range[1]}
                  total={decided.length}
                  noun="requests"
                  testId="decided-pager"
                />
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}

/** One request on one line, and its buttons — offered only when this caller may use them. */
function RequestRow({
  row,
  measure,
  balance,
  busy,
  onDecide,
  onReopen,
  coverage,
}: {
  row: TimeOffRequest;
  measure: (element: HTMLElement | null) => (() => void) | undefined;
  /** Their annual leave, beside a request still waiting (ST14). */
  balance: LeaveBalance | null;
  busy: boolean;
  onDecide: (id: string, what: "approve" | "decline", acknowledge?: boolean) => void;
  onReopen: (id: string) => void;
  coverage: Coverage | null;
}): React.JSX.Element {
  const meta: React.ReactNode[] = [];
  if (row.reason !== null && row.reason !== "") meta.push(<>&ldquo;{row.reason}&rdquo;</>);
  // Who typed it, when it was somebody else. Raising your own is the ordinary
  // case and said by the name on the line (ST15).
  if (row.filedOnBehalf) meta.push(`Logged by ${row.filedBy ?? "somebody who has since left"}`);
  if (row.decidedBy !== null) meta.push(`${STATUS_LABELS[row.status] ?? ""} by ${row.decidedBy}`);

  return (
    <div
      className={styles.req}
      ref={measure}
      data-ring="hr.request"
      data-ring-id={row.id}
      data-testid="time-off-row"
    >
      <div className={styles.reqBody}>
        <div className={styles.reqLine}>
          <b className={styles.reqName}>{row.name}</b>
          <span className={styles.typeTag}>{TYPE_LABELS[row.type]}</span>
          <span className={styles.reqWhen} data-testid="time-off-when">
            {row.startsOn === row.endsOn
              ? dayAndMonth(row.startsOn)
              : `${dayAndMonth(row.startsOn)} – ${dayAndMonth(row.endsOn)}`}
            {" · "}
            {/* What it costs against the allowance (S7), from their own rota. */}
            <span data-testid="time-off-days">{daysLabel(row.days)}</span>
          </span>
          {row.status !== "PENDING" && (
            <span
              className={row.status === "APPROVED" ? styles.goodTag : styles.plainTag}
              data-testid="time-off-status"
            >
              {STATUS_LABELS[row.status]}
            </span>
          )}
          {/* A waiting row still carries its word, for a screen reader and a filter. */}
          {row.status === "PENDING" && (
            <span className={styles.visuallyHidden} data-testid="time-off-status">
              {STATUS_LABELS[row.status]}
            </span>
          )}
        </div>

        {meta.length > 0 && (
          <p className={styles.reqMeta}>
            {meta.map((bit, index) => (
              <span key={index}>
                {index > 0 && " · "}
                {bit}
              </span>
            ))}
          </p>
        )}

        {row.decisionNote !== null && row.decisionNote !== "" && (
          <p className={styles.reqMeta} data-testid="time-off-decision-note">
            {row.decisionNote}
          </p>
        )}
      </div>

      {row.status === "PENDING" ? (
        <span
          className={
            balance?.remainingDays == null
              ? `${styles.leaveLeft} ${styles.leaveLeftNone}`
              : styles.leaveLeft
          }
          data-testid="leave-left"
        >
          {balance === null || balance.remainingDays === null ? (
            "No leave allowance set"
          ) : (
            <>
              <b className={balance.remainingDays < 0 ? styles.warmFigure : undefined}>
                {daysText(balance.remainingDays)}
              </b>{" "}
              of {daysText(balance.entitlementDays ?? 0)} days left
            </>
          )}
        </span>
      ) : (
        <span />
      )}

      <div className={styles.reqActs}>
        {/*
          **The row was asked, not the screen** (S8). Somebody looking at a
          request they filed sees no buttons, rather than two the endpoint then
          refuses.
        */}
        {row.canDecide && (
          <>
            <button
              type="button"
              className={`${styles.primary} ${styles.small}`}
              disabled={busy}
              onClick={() => onDecide(row.id, "approve")}
              data-testid="time-off-approve"
            >
              Approve
            </button>
            <button
              type="button"
              className={`${styles.ghost} ${styles.small}`}
              disabled={busy}
              onClick={() => onDecide(row.id, "decline")}
              data-testid="time-off-decline"
            >
              Decline
            </button>
          </>
        )}
        {row.status !== "PENDING" && (
          <button
            type="button"
            className={`${styles.ghost} ${styles.small}`}
            disabled={busy}
            onClick={() => onReopen(row.id)}
            data-testid="time-off-reopen"
          >
            Reopen
          </button>
        )}
      </div>

      {/*
        **The warning, once the server has held an approval back** (S9), on the
        row it is about and across its width, so the numbers and *Approve
        anyway* are on screen together.
      */}
      {coverage !== null && (
        <div className={styles.clash} data-testid="coverage-warning">
          <div className={styles.clashText}>
            {coverage.uncovered.length > 0 && (
              <p data-testid="coverage-uncovered">
                No provider would be rostered on{" "}
                {coverage.uncovered
                  .map((day) => `${dayAndMonth(day.date)} at ${day.branch}`)
                  .join(", ")}
                .
              </p>
            )}
            {coverage.appointments > 0 && (
              <p data-testid="coverage-appointments">
                {coverage.appointments === 1
                  ? "1 appointment names them as a provider in this range."
                  : `${String(coverage.appointments)} appointments name them as a provider in this range.`}
              </p>
            )}
          </div>
          <HelpTip label="About approving anyway">
            Approving anyway is a decision, not a mistake — the rota does not know who is coming in
            regardless.
          </HelpTip>
          <button
            type="button"
            className={`${styles.ghost} ${styles.small}`}
            disabled={busy}
            onClick={() => onDecide(row.id, "approve", true)}
            data-testid="time-off-approve-anyway"
          >
            Approve anyway
          </button>
        </div>
      )}
    </div>
  );
}

/** Every row here is this tall, which is what lets a page be counted. */
const BALANCE_ROW = 40;

/**
 * Leave balances — STORY-098, S10 — behind *Leave balances* in the title row
 * since ST15, taking the page's width.
 *
 * **Four figures, because they are four facts**: taken has happened, booked
 * ahead is committed, waiting is undecided and shown without being
 * subtracted, and left is what remains. **Annual leave only** draws the
 * allowance down, which the ? says.
 */
export function BalancesView({
  balances,
  year,
}: {
  balances: readonly LeaveBalance[] | null;
  year: number;
}): React.JSX.Element {
  const [areaRef, areaHeight] = useHeight<HTMLDivElement>();
  const [page, setPage] = useState(0);
  const rows = balances ?? [];
  const perPage =
    areaHeight > 0 ? Math.max(1, Math.floor(areaHeight / BALANCE_ROW)) : rows.length || 1;
  const count = Math.max(1, Math.ceil(rows.length / perPage));
  const current = Math.min(page, count - 1);
  const shown = rows.slice(current * perPage, current * perPage + perPage);

  return (
    <section className={`${styles.toCard} ${styles.toCardGrow}`} data-testid="balances">
      <div className={styles.cardHead}>
        <h3 className={styles.cardTitle}>Annual leave, {year}</h3>
        <HelpTip label="About leave balances">
          Annual leave only. Sick, unpaid and other time off is recorded with the requests and does
          not come out of an allowance.
        </HelpTip>
      </div>
      <div className={`${styles.balRow} ${styles.balHead}`} aria-hidden="true">
        <span>Person</span>
        <span>Allowance</span>
        <span>Taken</span>
        {/* Approved and still ahead; asked for and not yet decided (ST1). */}
        <span>Booked ahead</span>
        <span>Waiting</span>
        <span>Left</span>
      </div>
      <div className={styles.toArea} ref={areaRef}>
        {balances === null ? (
          <p className={styles.blank}>Reading&hellip;</p>
        ) : rows.length === 0 ? (
          <p className={styles.blank}>Nobody has been added yet.</p>
        ) : (
          shown.map((row) => (
            <div key={row.userId} className={styles.balRow} data-testid="balance-row">
              <span className={styles.balName}>{row.name}</span>
              {/*
                **Not set, never zero.** Zero is a real entitlement; its absence
                is a row nobody has filled in.
              */}
              <span data-testid="balance-allowance">
                {row.entitlementDays === null ? (
                  <span className={styles.dim}>not set</span>
                ) : (
                  balanceDays(row.entitlementDays)
                )}
              </span>
              <span data-testid="balance-taken">{balanceDays(row.takenDays)}</span>
              <span>{balanceDays(row.approvedDays)}</span>
              <span>{balanceDays(row.pendingDays)}</span>
              <span data-testid="balance-left">
                {row.remainingDays === null ? (
                  <span className={styles.dim}>&mdash;</span>
                ) : (
                  <b className={row.remainingDays < 0 ? styles.warmFigure : undefined}>
                    {balanceDays(row.remainingDays)}
                  </b>
                )}
              </span>
            </div>
          ))
        )}
      </div>
      <div className={styles.cardFoot}>
        <Pager
          page={current}
          count={count}
          onPage={setPage}
          from={current * perPage + 1}
          to={current * perPage + shown.length}
          total={rows.length}
          noun="people"
          testId="balances-pager"
        />
      </div>
    </section>
  );
}

/**
 * Raising one — for yourself, or for somebody who is not here to raise it — in
 * the *Ask for time off* dialog (ST1). **Who it is for is a field, not a
 * separate screen**: logging on behalf is the ordinary form with another name.
 */
export function RaiseDialog({
  people,
  meId,
  busy,
  onRaise,
  onClose,
}: {
  people: readonly StaffMember[] | null;
  meId: string | null;
  busy: boolean;
  onRaise: (body: RaiseTimeOff) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [userId, setUserId] = useState<string>("");
  const [type, setType] = useState<(typeof TIME_OFF_TYPES)[number]>("ANNUAL");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [reason, setReason] = useState("");

  const ready = startsOn !== "" && endsOn !== "";

  return (
    <Modal
      label="Ask for time off"
      testId="raise-dialog"
      onClose={onClose}
      panelClassName={styles.dialog}
    >
      <form
        className={styles.raiseForm}
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready) return;
          onRaise({
            ...(userId === "" ? {} : { userId }),
            type,
            startsOn,
            endsOn,
            ...(reason.trim() === "" ? {} : { reason: reason.trim() }),
          });
        }}
      >
        <div className={styles.dialogHead}>
          <h2 className={styles.dialogTitle}>Ask for time off</h2>
          <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Only when there is somebody else to ask for — Me lists no colleague (STORY-181e). */}
        {(people ?? []).some((person) => person.id !== meId) && (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              For
              <RequiredMark />
            </span>
            <select
              className={styles.input}
              value={userId}
              disabled={busy}
              aria-label="Who the time off is for"
              onChange={(event) => setUserId(event.target.value)}
              data-testid="raise-who"
            >
              <option value="">Me</option>
              {(people ?? [])
                .filter((person) => person.id !== meId)
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.firstName} {person.lastName}
                  </option>
                ))}
            </select>
          </label>
        )}

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            Type
            <RequiredMark />
          </span>
          <select
            className={styles.input}
            value={type}
            disabled={busy}
            aria-label="Type of leave"
            onChange={(event) => setType(event.target.value as (typeof TIME_OFF_TYPES)[number])}
            data-testid="raise-type"
          >
            {TIME_OFF_TYPES.map((option) => (
              <option key={option} value={option}>
                {TYPE_LABELS[option]}
              </option>
            ))}
          </select>
        </label>

        <div className={styles.pair}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              From
              <RequiredMark />
            </span>
            <input
              type="date"
              className={styles.input}
              value={startsOn}
              disabled={busy}
              aria-label="First day"
              onChange={(event) => {
                setStartsOn(event.target.value);
                // A single day is the common case, so the last day follows the
                // first until somebody moves it.
                if (endsOn === "" || endsOn < event.target.value) setEndsOn(event.target.value);
              }}
              data-testid="raise-from"
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              To
              <RequiredMark />
            </span>
            <input
              type="date"
              className={styles.input}
              value={endsOn}
              disabled={busy}
              aria-label="Last day"
              onChange={(event) => setEndsOn(event.target.value)}
              data-testid="raise-to"
            />
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Why</span>
          <input
            type="text"
            className={styles.input}
            maxLength={500}
            value={reason}
            disabled={busy}
            aria-label="Reason"
            onChange={(event) => setReason(event.target.value)}
            data-testid="raise-reason"
          />
        </label>

        <div className={styles.dialogFoot}>
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={busy || !ready}
            data-testid="raise-submit"
          >
            Ask
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** `2.5` → `2½ days`, `1` → `1 day`, `0` → `no working days`. */
function daysLabel(days: number): string {
  if (days === 0) return "no working days";
  return `${daysText(days)} ${days === 1 ? "day" : "days"}`;
}

/** `2.5` → `2½ d`, `0` → `—`. A dash reads faster than a zero in a table. */
function balanceDays(days: number): string {
  return days === 0 ? "—" : `${daysText(days)} d`;
}

const TYPE_LABELS: Record<string, string> = {
  ANNUAL: "Annual leave",
  SICK: "Sick leave",
  UNPAID: "Unpaid",
  OTHER: "Other",
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: "Waiting",
  APPROVED: "Approved",
  DECLINED: "Declined",
};
